import { useEffect, useState } from "react";

// Map of Tailwind media classes to their corresponding media query strings
const tailwindBreakpoints = {
  sm: "(min-width: 640px)",
  md: "(min-width: 768px)",
  lg: "(min-width: 1024px)",
  xl: "(min-width: 1280px)",
  "2xl": "(min-width: 1536px)",
} as const;

/**
 * Custom React hook to evaluate a CSS media query or Tailwind breakpoint and respond to changes.
 * @param queryOrBreakpoint - Tailwind breakpoint (e.g., 'sm', 'md') or custom CSS media query string
 * @returns boolean indicating whether the media query currently matches
 */
// Restrict the type of queryOrBreakpoint to valid Tailwind breakpoint keys or a custom string
export function useMediaQuery(
  queryOrBreakpoint: keyof typeof tailwindBreakpoints | (string & {})
): boolean {
  // Resolve the query string from Tailwind breakpoint or use the provided custom query
  // Ensure queryOrBreakpoint is a valid Tailwind breakpoint key before accessing tailwindBreakpoints
  const query =
    queryOrBreakpoint in tailwindBreakpoints
      ? tailwindBreakpoints[
          queryOrBreakpoint as keyof typeof tailwindBreakpoints
        ]
      : queryOrBreakpoint;

  const [matches, setMatches] = useState(
    () => window.matchMedia(query).matches
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    const handler = (event: MediaQueryListEvent) => setMatches(event.matches);

    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, [query]);

  return matches;
}
